/**
 * setupScore.js
 * -------------
 * Tiny frontend helper for marking Sophia Setup Score steps from any flow
 * (PostPurchaseSetup, GoLiveWizard, FastSetup test-call handler, etc.).
 *
 * Awaitable, but SAFE TO IGNORE — every existing caller fires and forgets, and
 * that still works unchanged. Returning the updated score lets a caller that
 * needs to know the mark actually landed (rather than assuming it did) await
 * it; the old version swallowed the response entirely, so a mark that failed
 * server-side was indistinguishable from one that succeeded.
 *
 * NEVER REJECTS — every failure path resolves to null, so an un-awaited call
 * can't produce an unhandled promise rejection.
 *
 * The backend endpoint is idempotent so retries / re-fires are safe.
 *
 * Valid step names (must match backend STEP_DEFINITIONS exactly):
 *   businessConfirmed | testCallCompleted | planActivated |
 *   phoneNumberAssigned | callForwardingDone | triageRulesSaved |
 *   contactsSynced | firstRealCall
 *
 * Usage:
 *   import { markSetupStep } from "../../lib/setupScore";
 *   markSetupStep("phoneNumberAssigned");   // no await needed — fires in background
 */
export const markSetupStep = async (step, agentId = null) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/onboarding/score/mark`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ step, agentId }),
    });
    if (!res.ok) {
      // A non-2xx used to vanish silently — fetch only rejects on network
      // failure, so an HTTP 4xx/5xx resolved and the old .catch() never ran.
      const body = await res.json().catch(() => ({}));
      console.warn(
        "[setupScore] mark failed for",
        step,
        `HTTP ${res.status}`,
        body?.message || "",
      );
      return null;
    }
    return await res.json();
  } catch (err) {
    console.warn("[setupScore] unexpected error for", step, err);
    return null;
  }
};

/**
 * Fetch the current setup score for the authenticated user and agent.
 * Returns { percent, steps, nextAction } on success, null on failure.
 */
export const fetchSetupScore = async (agentId = null) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    
    const url = new URL(`${process.env.REACT_APP_API_BASE_URL}/onboarding/score`);
    if (agentId) {
      url.searchParams.append("agentId", agentId);
    }

    const res = await fetch(url.toString(), {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.warn("[setupScore] fetch failed", err);
    return null;
  }
};

/**
 * Display catalogue for the 8 setup-score steps. Mirrors backend's
 * STEP_DEFINITIONS in order; labels + hints are user-facing copy used
 * by the score-breakdown popup. If you add a step on the backend, add
 * it here too — otherwise the popup falls back to the raw step name.
 */
export const SETUP_STEP_DISPLAY = [
  {
    key: "businessConfirmed",
    label: "Business confirmed",
    hint: "Add your business details to onboard",
    points: 15,
  },
  {
    key: "testCallCompleted",
    label: "Test call completed",
    hint: "Try Sophia from the web call test",
    points: 15,
  },
  {
    key: "phoneNumberAssigned",
    label: "Phone number assigned",
    hint: "Get a dedicated number for Sophia",
    points: 15,
  },
  {
    key: "callForwardingDone",
    label: "Call forwarding done",
    hint: "Configure forwarding from your business line",
    points: 10,
  },
  {
    key: "triageRulesSaved",
    label: "Triage rules saved",
    hint: "Tell Sophia when to transfer calls",
    points: 10,
  },
  {
    key: "contactsSynced",
    label: "Contacts synced",
    hint: "Help Sophia recognise your people",
    points: 10,
  },
  {
    key: "firstRealCall",
    label: "First real call",
    hint: "Get your first real customer call",
    points: 5,
  },
  {
    key: "planActivated",
    label: "Plan activated",
    hint: "Pick a plan to unlock full features",
    points: 20,
  },
];

/**
 * Given a setup score + the live agent record, work out which step the
 * user should resume on ("where they left off") — the same logic the old
 * SetupScoreCard used for its "Continue Setup" button. Returns the step
 * key, or null when everything is done.
 *
 * We pick the resume point from the `at` timestamps (latest-completed +1)
 * rather than "first missing in order", so a user who paid before
 * confirming their business still lands on the right next step. Paid users
 * get planActivated treated as done even if the score system never
 * received the mark, so they don't get routed into a checkout loop.
 */
export const computeSetupNextAction = (score, agent) => {
  const planLower = (agent?.agentPlan || "").toString().trim().toLowerCase();
  const hasPaidPlan =
    !!planLower && planLower !== "free" && planLower !== "demo";

  const isStepDone = (key) => {
    if (key === "planActivated") {
      return !!score?.steps?.[key]?.done || hasPaidPlan;
    }
    return !!score?.steps?.[key]?.done;
  };

  // Index of the step completed most recently (by timestamp), falling back
  // to highest-done-by-position when no timestamps are recorded.
  let lastDoneIdx = -1;
  let lastTs = -1;
  SETUP_STEP_DISPLAY.forEach((def, idx) => {
    if (!isStepDone(def.key)) return;
    const atRaw = score?.steps?.[def.key]?.at;
    const ts = atRaw ? new Date(atRaw).getTime() : 0;
    if (ts > lastTs) {
      lastTs = ts;
      lastDoneIdx = idx;
    } else if (lastTs <= 0 && idx > lastDoneIdx) {
      lastDoneIdx = idx;
    }
  });

  // 1. Next pending step AFTER the last completed one.
  for (let i = lastDoneIdx + 1; i < SETUP_STEP_DISPLAY.length; i++) {
    const def = SETUP_STEP_DISPLAY[i];
    if (!isStepDone(def.key)) return def.key;
  }
  // 2. Otherwise fill any earlier gap.
  for (const def of SETUP_STEP_DISPLAY) {
    if (!isStepDone(def.key)) return def.key;
  }
  // 3. Everything done.
  return null;
};
